import React, { useEffect, useState } from 'react';
import { CCard, CCardBody, CCardHeader, CCardTitle, CFormGroup, CLabel, CSelect } from '@coreui/react';
import { useMutation, useQueryClient } from 'react-query';
import _t from 'counterpart';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { extractErrorMessage, handleFormError } from '../../helpers';
import toast from 'react-hot-toast';
import { useGetSetting, useSetSetting } from './hooks';
import { SecurityLevel } from '../customers/types';

const DefaultCustomerSecurityLevel = () => {
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>(SecurityLevel.Regular);
  const [originalValue, setOriginalValue] = useState<SecurityLevel>(SecurityLevel.Regular);

  const queryClient = useQueryClient();
  const setSettingMutation = useSetSetting();

  const defaultSecurityLevelQuery = useGetSetting(
    'DEFAULT_CUSTOMER_SECURITY_LEVEL',
    'default-customer-security-level',
    {
      retry: false,
      refetchOnWindowFocus: false,
      onError: (e: any) => {
        toast.error(extractErrorMessage(e));
      },
    }
  );

  const updateMutation = useMutation(
    () => setSettingMutation.mutateAsync({ key: 'DEFAULT_CUSTOMER_SECURITY_LEVEL', value: String(securityLevel) }),
    {
      onSuccess: async () => {
        toast.success(_t('default-customer-security-level.successfully-updated'));
        await queryClient.invalidateQueries(['default-customer-security-level']);
        setOriginalValue(securityLevel);
        await defaultSecurityLevelQuery.refetch();
      },
      onError: async (e: any) => {
        if (e.response?.status !== 422) {
          handleFormError(e);
        }
      },
    }
  );

  useEffect(() => {
    if (defaultSecurityLevelQuery.data !== undefined) {
      const level = parseInt(String(defaultSecurityLevelQuery.data), 10) as SecurityLevel;
      setSecurityLevel(level);
      setOriginalValue(level);
    }
  }, [defaultSecurityLevelQuery.data]);

  const hasChanges = securityLevel !== originalValue;

  const invokeMutation = () => {
    updateMutation.mutate();
  };

  if (defaultSecurityLevelQuery.isLoading) {
    return (
      <CCard className="mt-4">
        <CCardHeader>
          <CCardTitle>{_t('default-customer-security-level.title')}</CCardTitle>
        </CCardHeader>
        <CCardBody>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard className="mt-4">
      <CCardHeader>
        <CCardTitle>{_t('default-customer-security-level.title')}</CCardTitle>
        <p className="mb-0 mt-1 small">{_t('default-customer-security-level.description')}</p>
      </CCardHeader>
      <CCardBody>
        <CFormGroup>
          <CSelect
            value={String(securityLevel)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSecurityLevel(parseInt(e.target.value, 10) as SecurityLevel)
            }
            disabled={defaultSecurityLevelQuery.isLoading || updateMutation.isLoading}
            className="mt-2"
          >
            <option value={SecurityLevel.Regular}>{_t('default-customer-security-level.level-option-regular')}</option>
            <option value={SecurityLevel.Sensitive}>
              {_t('default-customer-security-level.level-option-sensitive')}
            </option>
          </CSelect>
          <small className="text-muted mt-2 d-block">{_t('default-customer-security-level.help-text')}</small>
        </CFormGroup>

        <ButtonWithLoader
          isLoading={updateMutation.isLoading}
          onClick={invokeMutation}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('action.save')}
          className="btn-md"
          showText={false}
          disabled={!hasChanges}
        />
      </CCardBody>
    </CCard>
  );
};

export default DefaultCustomerSecurityLevel;
